import { Card, CardBody, CardTitle, Badge } from 'reactstrap';
import { Link } from 'react-router-dom';
import { TournamentRecurringType } from '@repo/common-types';
import { useGetTournamentSchedulesQuery } from '@/redux/api/tournaments';

interface TournamentScheduleCardProps {
  id: string;
}

const TournamentScheduleCard = ({ id }: TournamentScheduleCardProps) => {
  const { schedule, isLoading } = useGetTournamentSchedulesQuery(undefined, {
    selectFromResult: ({ data, isLoading }) => ({
      schedule: data?.find((s) => s.id === id),
      isLoading,
    }),
  });

  if (isLoading) {
    return (
      <Card className="mb-3">
        <CardBody>
          <div className="text-muted text-center">Loading...</div>
        </CardBody>
      </Card>
    );
  }

  if (!schedule) {
    return (
      <Card className="mb-3">
        <CardBody>
          <div className="text-muted text-center">Schedule not found</div>
        </CardBody>
      </Card>
    );
  }

  const getRecurringTypeLabel = (type: TournamentRecurringType) => {
    switch (type) {
      case TournamentRecurringType.DAILY:
        return 'Daily';
      case TournamentRecurringType.WEEKLY:
        return 'Weekly';
      case TournamentRecurringType.MONTHLY:
        return 'Monthly';
      case TournamentRecurringType.HOURLY:
        return 'Hourly';
      default:
        return type;
    }
  };

  const getFrequencyLabel = (type: TournamentRecurringType | null, frequency: number | null) => {
    if (!type || !frequency) {
      return 'Unknown';
    }
    if (frequency === 1) {
      return getRecurringTypeLabel(type);
    }
    switch (type) {
      case TournamentRecurringType.HOURLY:
        return `Every ${frequency} hours`;
      case TournamentRecurringType.DAILY:
        return `Every ${frequency} days`;
      case TournamentRecurringType.WEEKLY:
        return `Every ${frequency} weeks`;
      case TournamentRecurringType.MONTHLY:
        return `Every ${frequency} months`;
      default:
        return `Every ${frequency} ${type.toLowerCase()}`;
    }
  };

  return (
    <Card className="mb-3">
      <CardBody>
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            <CardTitle tag="h5" className="mb-2">
              {schedule?.template?.name || `Template ${schedule?.templateId?.slice(-8)}`}
            </CardTitle>
            <div className="mb-3">
              <Badge
                color={
                  schedule.recurringType === TournamentRecurringType.HOURLY
                    ? 'warning'
                    : schedule.recurringType === TournamentRecurringType.DAILY
                      ? 'primary'
                      : schedule.recurringType === TournamentRecurringType.WEEKLY
                        ? 'info'
                        : 'success'
                }
                className="me-2"
              >
                {getFrequencyLabel(schedule.recurringType, schedule.recurringFrequency)}
              </Badge>
            </div>
            <div className="row">
              <div className="col-md-4">
                <div className="mb-2">
                  <strong>Template:</strong>
                  <br />
                  {schedule.template?.name ? (
                    <Link
                      to={`/tournament/templates?templateId=${schedule.templateId}`}
                      className="text-primary text-decoration-underline"
                    >
                      {schedule.template.name}
                    </Link>
                  ) : (
                    <span className="text-muted">Template {schedule.templateId.slice(-8)}</span>
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-2">
                  <strong>Last Iteration:</strong>
                  <br />
                  {schedule.lastIteration ? (
                    <div>
                      <Link
                        to={`/tournaments/details/${schedule.lastIteration.id}`}
                        className="text-primary text-decoration-underline"
                      >
                        {schedule.lastIteration.name}
                      </Link>
                      <br />
                      <small className="text-muted">
                        {schedule.lastIteration.startTime
                          ? new Date(schedule.lastIteration.startTime).toLocaleString()
                          : 'No start time'}
                      </small>
                    </div>
                  ) : (
                    <span className="text-muted">None yet</span>
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <div className="mb-2">
                  <strong>Next Run:</strong>
                  <br />
                  {schedule.nextIteration ? (
                    <div>
                      <Link
                        to={`/tournaments/details/${schedule.nextIteration.id}`}
                        className="text-primary text-decoration-underline"
                      >
                        {schedule.nextIteration.name}
                      </Link>
                      <br />
                      <small className="text-muted">
                        {schedule.nextIteration.startTime
                          ? new Date(schedule.nextIteration.startTime).toLocaleString()
                          : 'No start time'}
                      </small>
                    </div>
                  ) : (
                    <span className="text-muted">None scheduled</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default TournamentScheduleCard;
